/** Where the sync and ad spend stand, judged once (plan 1-3).
 *
 *  Google Ads answers 403 until the API access is approved, so "no spend
 *  data" and "spent $0" look the same in the totals. They are told apart here
 *  from data: the Google status (`ads_linked`, `campaign_count`) and the
 *  details the server writes on each sync run (`details_json`: `admob_error`,
 *  `ads_error` …, see internal/sync `Result.DetailsJSON`). A run stays `ok`
 *  when only Ads was refused, so `last_sync_at` keeps moving; the refusal is in
 *  the details. Once the access is approved the numbers fill in with no code
 *  change. */

import type { GoogleStatus } from '@/api/client'
import type { JobRun } from '@/admin-design/_common/jobs/types'
import { statusOf } from './status'

/** One sync pass's result, as the run's details_json carries it. */
export interface SyncDetails {
  admob_ok?: boolean
  admob_rows?: number
  admob_error?: string
  admob_skipped?: boolean
  ads_ok?: boolean
  ads_rows?: number
  ads_error?: string
  degraded?: boolean
  apple_ok?: boolean
  apple_rows?: number
  apple_error?: string
  /** true = no Apple credentials stored; false = Apple was tried. */
  apple_skipped?: boolean
  /** App Store proceeds copied from aso-saas. `ios_revenue_error` may come with
   *  ok = true: it then names rows left out for lack of an exchange rate. */
  ios_revenue_ok?: boolean
  ios_revenue_rows?: number
  ios_revenue_error?: string
  /** true = not attempted (no key, or no Apple campaign). */
  ios_revenue_skipped?: boolean
  duration_ms?: number
}

/** A run's details, or null when it has none (a run that failed before the
 *  pass started stores "{}") or they do not parse. */
export function syncDetails(run: Pick<JobRun, 'details_json'> | null | undefined): SyncDetails | null {
  const raw = run?.details_json
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as unknown
    if (!v || typeof v !== 'object' || Array.isArray(v) || Object.keys(v).length === 0) return null
    return v as SyncDetails
  } catch {
    return null
  }
}

const time = (iso: string | undefined) => {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? 0 : t
}

/** The sync runs that matter, from a list in any order: the newest of all,
 *  the newest that finished, and the newest finished one that carries a pass
 *  result (the one to read AdMob and Ads errors from). */
export function newestSyncRuns(runs: JobRun[] | null | undefined): {
  latest?: JobRun
  finished?: JobRun
  withDetails?: JobRun
} {
  const sorted = [...(runs ?? [])].filter((r) => r.job === 'sync').sort((a, b) => time(b.started_at) - time(a.started_at))
  const finished = sorted.filter((r) => r.status !== 'running')
  return {
    latest: sorted[0],
    finished: finished[0],
    withDetails: finished.find((r) => syncDetails(r) !== null),
  }
}

/** The last ok pass this long ago, with nothing running, is 오래됨: the
 *  hourly sync has missed two runs. */
export const STALE_MS = 3 * 3_600_000

export type SyncKey = 'never' | 'running' | 'ok' | 'partial' | 'error' | 'stale'

/** The sync's state (status domain `sync`). In order: a run under way is
 *  실행 중; the newest finished run failing is 실패; the last ok pass over three
 *  hours old is 오래됨 (checked before 일부 실패 — a stopped sync matters more
 *  than one that half-works); an ok run with an AdMob error in its details
 *  is 일부 실패; no run at all is 실행 전.
 *
 *  A Google Ads error alone leaves it 정상: the 광고비 state (spendState)
 *  says it right beside, and the API refusal lasts weeks — an orange 일부 실패
 *  on top would say the same thing twice, every hour, on every 오늘. */
export function syncKey(input: { runs?: JobRun[] | null; status?: GoogleStatus | null; now?: Date }): SyncKey {
  const { latest, finished } = newestSyncRuns(input.runs)
  if (latest?.status === 'running') return 'running'
  if (!finished) return input.status?.last_sync_at ? staleOr(input, 'ok') : 'never'
  if (finished.status === 'error') return 'error'
  const stale = staleOr(input, null)
  if (stale) return stale
  if (finished.status === 'ok') {
    const d = syncDetails(finished)
    return d?.admob_error ? 'partial' : 'ok'
  }
  // skipped or cancelled: nothing new came in, nothing broke.
  return 'ok'
}

function staleOr<T extends SyncKey | null>(input: { runs?: JobRun[] | null; status?: GoogleStatus | null; now?: Date }, otherwise: T): 'stale' | T {
  const now = (input.now ?? new Date()).getTime()
  const lastOk =
    time(input.status?.last_sync_at) ||
    Math.max(0, ...(input.runs ?? []).filter((r) => r.job === 'sync' && r.status === 'ok').map((r) => time(r.finished_at || r.started_at)))
  if (lastOk > 0 && now - lastOk > STALE_MS) return 'stale'
  return otherwise
}

export type SpendState = 'unlinked' | 'blocked' | 'error' | 'nodata' | 'ok'

/** Google refused the call (API access not approved, no permission on the
 *  account) rather than the call itself failing. */
export function isAdsRefusal(message: string): boolean {
  return /\b403\b|PERMISSION_DENIED|NOT_APPROVED|ACTION_NOT_PERMITTED/.test(message)
}

/** Where ad spend stands (status domain `spend`): Ads not linked; Ads linked
 *  but the last pass was refused (`blocked`) or failed some other way
 *  (`error`, e.g. a query Google rejects); linked and allowed but no
 *  campaign; or spend comes in. */
export function spendState(input: { status?: GoogleStatus | null; runs?: JobRun[] | null }): SpendState {
  const d = syncDetails(newestSyncRuns(input.runs).withDetails)
  if (!input.status?.ads_linked) {
    // Apple is connected on its own: its campaigns show without Google.
    if (d?.apple_skipped === false) return (input.status?.campaign_count ?? 0) > 0 ? 'ok' : 'nodata'
    return 'unlinked'
  }
  if (d?.ads_error) return isAdsRefusal(d.ads_error) ? 'blocked' : 'error'
  if ((input.status.campaign_count ?? 0) === 0) return 'nodata'
  return 'ok'
}

/** Whether spend, profit and ROAS cells show numbers: yes when spend data
 *  comes in (0 is then a real $0.00), a dash when it cannot. */
export function spendShown(state: SpendState): boolean {
  return state === 'ok' || state === 'nodata'
}

/** The line a tile shows in place of spend that cannot come in:
 *  "광고비 없음 · Ads 접근 거부". Null when spend is shown. */
export function spendHint(state: SpendState): string | null {
  return spendShown(state) ? null : `광고비 없음 · ${statusOf('spend', state).label}`
}

/** The Google Ads refusal or error, word for word, for a notice (monospace). */
export function adsError(runs: JobRun[] | null | undefined): { message: string; at?: string } | null {
  const run = newestSyncRuns(runs).withDetails
  const msg = syncDetails(run)?.ads_error
  return msg ? { message: msg, at: run?.finished_at || run?.started_at } : null
}

/** The Apple Ads error of the last pass, word for word, for a notice. Null
 *  when Apple is not set up or came in fine. */
export function appleError(runs: JobRun[] | null | undefined): { message: string; at?: string } | null {
  const run = newestSyncRuns(runs).withDetails
  const msg = syncDetails(run)?.apple_error
  return msg ? { message: msg, at: run?.finished_at || run?.started_at } : null
}

/** The App Store revenue pull's error of the last pass, word for word. Null
 *  when it came in fine or was not tried. */
export function iosRevenueError(runs: JobRun[] | null | undefined): { message: string; ok: boolean; at?: string } | null {
  const run = newestSyncRuns(runs).withDetails
  const d = syncDetails(run)
  return d?.ios_revenue_error ? { message: d.ios_revenue_error, ok: !!d.ios_revenue_ok, at: run?.finished_at || run?.started_at } : null
}

/** True when the last pass did not try the revenue pull (no aso-saas key).
 *  False for a run without the field: an older server never skipped on purpose. */
export function iosRevenueSkipped(runs: JobRun[] | null | undefined): boolean {
  return syncDetails(newestSyncRuns(runs).withDetails)?.ios_revenue_skipped === true
}

export type AdmobKey = 'unset' | 'error' | 'ok'

/** The AdMob side (status domain `admob`): no publisher ID, the last pass's
 *  AdMob error, or fine. */
export function admobKey(input: { status?: GoogleStatus | null; runs?: JobRun[] | null }): AdmobKey {
  if (!input.status?.admob_account_id) return 'unset'
  const d = syncDetails(newestSyncRuns(input.runs).withDetails)
  return d?.admob_error ? 'error' : 'ok'
}
